import { resolveName } from 'components/form/final-form/hooks/useField'
import { PrivateRoute } from 'components/route'
import { FormApi } from 'final-form'
import { TipoExameEnum } from 'graphql/types.generated'
import { useMemo } from 'react'
import { useField } from 'react-final-form'
import { Switch, useHistory, useRouteMatch } from 'react-router'
import { emptyArrayOf } from 'util/array'
import { metaPath } from 'util/metaPath'
import { SoapState } from 'view/atendimentos/atendimento-individual/model'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import { ProblemaCondicaoModel } from '../../avaliacao/components/problemas-condicoes/model-problemasCondicoes'
import { SolicitacaoExameQueryModel } from '../../objetivo/resultados-exames/model-resultadosExames'
import { grupoCboSolicitacaoExame } from '../acessos'
import { RequisicaoExameCitopatologicoModal } from './components/requisicao-exame/citopatologico/RequisicaoExameCitopatologicoModal'
import { RequisicaoExameMamografiaModal } from './components/requisicao-exame/mamografia/RequisicaoExameMamografiaModal'
import { SolicitacaoExameModel } from './model-solicitacaoExames'
import SolicitarExamesEditarModal from './SolicitarExamesEditarModal'
import SolicitarExamesFormModal from './SolicitarExamesFormModal'
import SolicitarExamesReplicarModal from './SolicitarExamesReplicarModal'

export interface SolicitarExamesRootViewProps {
  cidadao: CidadaoAtendimento
  initialValues?: Partial<SolicitacaoExameModel>
  adicionados: SolicitacaoExameModel[]
  existentes: ReadonlyArray<SolicitacaoExameQueryModel>
  iniciadoEm: Instant
  handleSubmit(formValues: SolicitacaoExameModel, formApi: FormApi): void
  handleUpdate(formValues: SolicitacaoExameModel): void
}

const urlSolicitacaoExame = '/solicitacao-exame/'
const urlSolicitacao = '/solicitacao/'
export const urlSolicitacaoExameComum = urlSolicitacaoExame + 'comum/'
export const urlSolicitacaoExameAltoCusto = urlSolicitacaoExame + 'alto-custo/'
export const urlSolicitacaoProcedOci = urlSolicitacao + 'procedimento-oci/'
export const urlSolicitacaoExameEditar = urlSolicitacaoExame + 'editar/'
export const urlSolicitacaoExameReplicar = urlSolicitacaoExame + 'replicar/'
export const urlSolicitacaoExameCitopatologico = urlSolicitacaoExame + 'citopatologico/'
export const urlSolicitacaoExameMamografia = urlSolicitacaoExame + 'mamografia/'

const soapPath = metaPath<SoapState>()

export function SolicitarExamesRootView(props: SolicitarExamesRootViewProps) {
  const { cidadao, adicionados, existentes, initialValues, iniciadoEm, handleSubmit, handleUpdate } = props
  const match = useRouteMatch()
  const history = useHistory()

  const {
    input: { value: condicoesAvaliadasNoAtendimento },
  } = useField<ProblemaCondicaoModel[]>(resolveName(soapPath.avaliacao.problemasECondicoes), {
    subscription: { value: true },
  })

  let initialValue = initialValues as SolicitacaoExameModel

  if (condicoesAvaliadasNoAtendimento?.length > 0) {
    const condicoesCid10 = condicoesAvaliadasNoAtendimento.filter((condicao) => {
      return condicao.cid?.id !== undefined
    })

    if (condicoesCid10.length === 1) {
      initialValue = { ...initialValues, cid10: condicoesCid10[0].cid } as SolicitacaoExameModel
    }
  }

  const idsExmAdicionadosAtend = useMemo(() => {
    if (Array.isArray(adicionados)) {
      const examesSolicitados = adicionados.flatMap((solicitacao) => solicitacao.examesSolicitados)
      const exameIds = examesSolicitados.map((exame) => exame.exame.id)
      const procedIds = exameIds ?? emptyArrayOf<string>()

      return new Set<ID>(procedIds)
    }

    return new Set<ID>()
  }, [adicionados])

  return (
    <Switch>
      <PrivateRoute path={`${match.url}${urlSolicitacaoExameComum}`} permission={grupoCboSolicitacaoExame.adicionar}>
        <SolicitarExamesFormModal
          cidadao={cidadao}
          initialValues={{ ...initialValue, tipoExame: TipoExameEnum.COMUM }}
          editing={false}
          onSubmit={handleSubmit}
          idsExmAdicionadosAtend={idsExmAdicionadosAtend}
          iniciadoEm={iniciadoEm}
          onClose={() => history.push(match.url.replace(urlSolicitacaoExameComum, ''))}
        />
      </PrivateRoute>
      <PrivateRoute
        path={`${match.url}${urlSolicitacaoExameAltoCusto}`}
        permission={grupoCboSolicitacaoExame.adicionar}
      >
        <SolicitarExamesFormModal
          cidadao={cidadao}
          initialValues={{ ...initialValue, tipoExame: TipoExameEnum.ALTO_CUSTO }}
          editing={false}
          onSubmit={handleSubmit}
          idsExmAdicionadosAtend={idsExmAdicionadosAtend}
          iniciadoEm={iniciadoEm}
          onClose={() => history.push(match.url.replace(urlSolicitacaoExameAltoCusto, ''))}
        />
      </PrivateRoute>
      <PrivateRoute path={`${match.url}${urlSolicitacaoProcedOci}`} permission={grupoCboSolicitacaoExame.adicionar}>
        <SolicitarExamesFormModal
          cidadao={cidadao}
          initialValues={{ ...initialValue, tipoExame: TipoExameEnum.OCI }}
          editing={false}
          onSubmit={handleSubmit}
          idsExmAdicionadosAtend={idsExmAdicionadosAtend}
          iniciadoEm={iniciadoEm}
          onClose={() => history.push(match.url.replace(urlSolicitacaoProcedOci, ''))}
        />
      </PrivateRoute>
      <PrivateRoute
        path={`${match.url}${urlSolicitacaoExameEditar}:id`}
        permission={grupoCboSolicitacaoExame.adicionar}
      >
        <SolicitarExamesEditarModal
          cidadao={cidadao}
          onSubmit={handleUpdate}
          adicionados={adicionados}
          idsExmAdicionadosAtend={idsExmAdicionadosAtend}
          iniciadoEm={iniciadoEm}
          onClose={() => history.push(match.url.split(urlSolicitacaoExameEditar)[0])}
        />
      </PrivateRoute>
      <PrivateRoute
        path={`${match.url}${urlSolicitacaoExameReplicar}:id`}
        permission={grupoCboSolicitacaoExame.adicionar}
      >
        <SolicitarExamesReplicarModal
          cidadao={cidadao}
          onSubmit={handleSubmit}
          existentes={existentes}
          idsExmAdicionadosAtend={idsExmAdicionadosAtend}
          onClose={() => history.push(match.url.split(urlSolicitacaoExameReplicar)[0])}
        />
      </PrivateRoute>
      <PrivateRoute
        path={`${match.url}${urlSolicitacaoExameCitopatologico}:id`}
        permission={grupoCboSolicitacaoExame.adicionar}
      >
        <RequisicaoExameCitopatologicoModal
          onClose={() => history.push(match.url.split(urlSolicitacaoExameCitopatologico)[0])}
          handleUpdate={handleUpdate}
        />
      </PrivateRoute>
      <PrivateRoute
        path={`${match.url}${urlSolicitacaoExameMamografia}:id`}
        permission={grupoCboSolicitacaoExame.adicionar}
      >
        <RequisicaoExameMamografiaModal
          onClose={() => history.push(match.url.split(urlSolicitacaoExameMamografia)[0])}
        />
      </PrivateRoute>
    </Switch>
  )
}
